import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  FileText, 
  CreditCard, 
  AlertTriangle,
  TrendingUp,
  Clock,
  ShieldAlert,
  RefreshCw
} from 'lucide-react';

const ADMIN_API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/secure-admin`;

export const AdminDashboardPage = () => {
  const { token, admin } = useAdminAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${ADMIN_API_URL}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load dashboard');
      }

      const data = await response.json();
      setDashboard(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // Refresh every 60 seconds
    const interval = setInterval(fetchDashboard, 60000);
    return () => clearInterval(interval);
  }, [token]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-gray-400">Welcome back, {admin?.name}</p>
        </div>
        <button
          onClick={fetchDashboard}
          className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-800"
          data-testid="refresh-dashboard"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <Alert variant="destructive" className="bg-red-900/50 border-red-800">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Alerts */}
      {dashboard?.alerts?.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-yellow-500" />
            Active Alerts
          </h2>
          {dashboard.alerts.map((alert, i) => (
            <Alert 
              key={i} 
              className={`${
                alert.severity === 'high' 
                  ? 'bg-red-900/30 border-red-700' 
                  : 'bg-yellow-900/30 border-yellow-700'
              }`}
            >
              <AlertTriangle className={`h-4 w-4 ${
                alert.severity === 'high' ? 'text-red-400' : 'text-yellow-400'
              }`} />
              <AlertDescription className={
                alert.severity === 'high' ? 'text-red-200' : 'text-yellow-200'
              }>
                <span className="font-semibold">{alert.type.replace(/_/g, ' ').toUpperCase()}</span>
                : {alert.message}
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Users"
          value={dashboard?.kpis?.total_users || 0}
          subValue={`+${dashboard?.kpis?.new_users_24h || 0} today`}
          icon={Users}
          color="blue"
        />
        <KPICard
          title="Total Jobs"
          value={dashboard?.kpis?.total_jobs || 0}
          icon={FileText}
          color="green"
        />
        <KPICard
          title="Total Services"
          value={dashboard?.kpis?.total_services || 0}
          icon={TrendingUp}
          color="purple"
        />
        <KPICard
          title="Payments (24h)"
          value={dashboard?.kpis?.payments_24h || 0}
          icon={CreditCard}
          color="orange"
        />
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5" />
              System Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <StatusRow label="API Status" status="operational" />
            <StatusRow label="Database" status="operational" />
            <StatusRow label="Email Service" status="operational" />
            <StatusRow label="Payment Gateway" status="operational" />
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Quick Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Your Role</span>
              <Badge className="bg-blue-600 capitalize">{admin?.role}</Badge>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Session</span>
              <span className="text-green-400">Active</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Last Updated</span>
              <span className="text-gray-300">
                {dashboard?.generated_at 
                  ? new Date(dashboard.generated_at).toLocaleTimeString()
                  : 'N/A'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const KPICard = ({ title, value, subValue, icon: Icon, color }) => {
  const colors = {
    blue: 'bg-blue-600',
    green: 'bg-green-600',
    purple: 'bg-purple-600',
    orange: 'bg-orange-600'
  };

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-gray-400 text-sm">{title}</p>
            <p className="text-3xl font-bold text-white mt-1">{value.toLocaleString()}</p>
            {subValue && <p className="text-green-400 text-xs mt-1">{subValue}</p>}
          </div>
          <div className={`${colors[color]} p-3 rounded-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const StatusRow = ({ label, status }) => (
  <div className="flex justify-between items-center">
    <span className="text-gray-400 text-sm">{label}</span>
    <Badge 
      className={status === 'operational' 
        ? 'bg-green-600/20 text-green-400 border-green-600' 
        : 'bg-red-600/20 text-red-400 border-red-600'}
    >
      {status}
    </Badge>
  </div>
);

export default AdminDashboardPage;
